import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Grid } from '@mui/material';
import './entryFormReview.css';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { calcDogEntryFee, calcOwnerEntryFees } from '../../../utils/calculateFees';
import { EntryFormEventInfo } from '../../../models/EntryFormEventInfo';
import { EntryFormEventInfoClass } from '../../../models/EntryFormEventInfoClass';
import { EntryFormRecordData } from '../../../models/EntryFormRecordData';
import { setAgreeToAgreementDate, setEntries, setEntryForms, setStepIndex, setSumCatalogFee } from '../../../redux/reducers/entryFormsSlice';
import EntryFormAgreement from '../../atoms/entryFormAgreement/entryFormAgreement';
import EntryFormPrint from '../../atoms/entryFormPrint/entryFormPrint';
import _ from 'lodash';

export default function EntryFormReview() {

  const { entries, agreeToAgreementDate, sumCatalogFee } = useAppSelector((state: any) => state.entryForms);
  const { catalogFee, entryEvents, serviceFee, serviceFeeBasis } = useAppSelector((state: any) => state.eventConfig);
  
  const [ isCatalogRequested, setIsCatalogRequested ] = useState(sumCatalogFee > 0 ? true : false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const updatedEntries: EntryFormRecordData[] = [];
    entries.forEach((entry: EntryFormRecordData) => {
      const index = _.findIndex(updatedEntries, (val: EntryFormRecordData) => val.akcRegistrationNum === entry.akcRegistrationNum && val.callName === entry.callName);
      // exclude the entry form if it's blank (entryFee = 0) or we already have a form for this callName and AKC number
      if (entry.entryFee > 0 && index === -1) {
        updatedEntries.push(_.cloneDeep(entry));
      }
      if (index > -1) {
        // merge multiple entry forms for same dog
        entry.eventEntries.forEach((eventEntry: EntryFormEventInfo) => {
          eventEntry.classes.forEach((eventEntryClass: EntryFormEventInfoClass) => {
            if (eventEntryClass.isEntered) {
              updateEventEntryClass(updatedEntries[index], eventEntry.eventId, eventEntryClass);
            }
          });
        });
        const calcDogEntryFeeResult = calcDogEntryFee(updatedEntries[index], entryEvents);
        updatedEntries[index].entryFee = calcDogEntryFeeResult.entryFee;
      }
     });
    const calcOwnerEntryFeesResult = calcOwnerEntryFees(updatedEntries, serviceFee, serviceFeeBasis, sumCatalogFee);
    dispatch(setEntryForms({totalFees: calcOwnerEntryFeesResult.totalFees, sumEntryFee: calcOwnerEntryFeesResult.sumEntryFee, totalServiceFee: calcOwnerEntryFeesResult.sumServiceFee }));
    dispatch(setEntries(updatedEntries));
  }, []);

  const updateEventEntryClass = (targetEntry: EntryFormRecordData, eventId: string, srcClass: EntryFormEventInfoClass) => {
    const eventEntryIndex = _.findIndex(targetEntry.eventEntries, (val: EntryFormEventInfo) => val.eventId === eventId);
    if (eventEntryIndex > -1) {
      const classesIndex = _.findIndex(targetEntry.eventEntries[eventEntryIndex].classes, (val: EntryFormEventInfoClass) => val.name === srcClass.name);
      if (classesIndex > -1) {
        if (!targetEntry.eventEntries[eventEntryIndex].classes[classesIndex].isEntered) {
          targetEntry.eventEntries[eventEntryIndex].classes[classesIndex].isEntered = true;
          targetEntry.eventEntries[eventEntryIndex].classes[classesIndex].jumpHt = srcClass.jumpHt;
          targetEntry.eventEntries[eventEntryIndex].classes[classesIndex].teamName = srcClass.teamName;
        }
      }
    }
  }

  const getClasses = (eventEntries: EntryFormEventInfo[]) => {
    const retValue: string[] = [];
    eventEntries.forEach((eventEntry: EntryFormEventInfo) => {
      eventEntry.classes.forEach((eventEntryClass: any) => {
        if (eventEntryClass.isEntered) {
          retValue.push(`${eventEntry.eventId} : ${eventEntryClass.name} - Jump Ht: ${eventEntryClass.jumpHt ? eventEntryClass.jumpHt : 'blank'}${ eventEntryClass.teamName ? ' Team: ' + eventEntryClass.teamName : ''}`);
        }
      });
    });
    return retValue;
  }
  
  const getEntryFee = (entry: EntryFormRecordData) => {
    return entry.entryFee && typeof entry.entryFee === 'number' ? entry.entryFee.toFixed(2) : 'unknown';
  }

  const updateCatalogFee = (isChecked: boolean) => {
    setIsCatalogRequested(isChecked);
    if (isChecked) {
      dispatch(setSumCatalogFee(catalogFee));
    } else {
      dispatch(setSumCatalogFee(0));
    }
  }

  const getDollarAmount = (val: number) => {
    return val && !isNaN(val) ? val.toFixed(2) : '0';
  }

  return (
    <>
      <Grid key={'entry-form-data-header'} item md={12} className='noprint'>
        <h2>Review the Data below and go back to the Entry</h2>
      </Grid>
      { entries.length === 0 ?
        <>
          <div id="entry-form-review-no-entries">
            You have no completed entries. Please return to Step 1 to complete and save at least one entry.
          </div>
          <Grid container spacing={2} justifyContent="center" p={2} className='noprint'>
            <Grid item md={4} p={2}>
              <Button 
                id="entry-form-review-fix-data-button-no-entries"
                color="primary" 
                variant="contained"
                style={{textTransform: 'none'}}
                onClick={(e: any) => { dispatch(setStepIndex(0)); dispatch(setAgreeToAgreementDate('')) }}>
                Oopps, Return to Step 1 to add data
              </Button>
            </Grid>
          </Grid>
        </>
        :
        <>
          <Grid container alignItems="center" alignContent="center" justifyContent="center" className='noprint'>
            <Grid item md={12} p={2}>
              <div id="entry-form-review-instructions">
                You have successfully completed {entries.length} entry form{ entries.length > 1 ? 's' : '' }.
                Please review { entries.length > 1 ? 'them' : 'it' } below, and review and click 'I Agree' to the Agreement before proceeding to the 'Looks Good, Proceed to Payment' step. 
                Pay particular attention to the email address and AKC Registration.
              </div>
            </Grid>
            { getDollarAmount(catalogFee) > '0' ?
              <Grid item md={12} p={1}>
                <div>
                  <Checkbox 
                    id="entry-form-review-catalog-request-checkbox"
                    checked={isCatalogRequested}
                    onChange={(e: any) => updateCatalogFee(e.target.checked)}
                    color="primary" />
                  <span id='entry-form-review-catalog-request-text'>Yes, I want to pre-order a catalog for ${ getDollarAmount(catalogFee) }</span>
                </div>
              </Grid>
              : null
            }
            { entries.map((entry: EntryFormRecordData, index: number) => (
              <Grid container direction="column" justifyContent="flex-start" p={2} key={'entry-form-container-' + index}>
                <Grid item className="entry-form-review-dog-header" id={`entry-form-review-entries-${index}-call-name`}>Call Name: { entry.callName ? entry.callName : entry.akcRegistrationNum }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-1`}>Breed: { entry.breed ? entry.breed : 'blank' } Variety: { entry.variety  ? entry.variety : 'blank' } Sex: { entry.sex  ? entry.sex : 'blank' }
                </Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-2`}>Junior Handler: { entry.jrHandlerName === '' ? 'blank' : entry.jrHandlerName + '/' + entry.jrHandlerNumber }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-3`}>Full AKC Name: { entry.akcName }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-4`}>Call Name: { entry.callName ? entry.callName : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-5`} className="entry-form-review-bold">AKC Number: { entry.akcRegistrationNum }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-6`}>Date of Birth: { entry.dateOfBirth ? entry.dateOfBirth : 'blank' } Country of Birth: { entry.countryOfBirth ? entry.countryOfBirth  : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-7`}>Breeder: { entry.breeder ? entry.breeder : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-8`}>Sire: { entry.sire ? entry.sire : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-9`}>Dam: { entry.dam ? entry.dam : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-10`}>Owner(s): { entry.owners }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-11`}>Address: { entry.ownerAddress.street ? entry.ownerAddress.street : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-12`}>City: { entry.ownerAddress.city ? entry.ownerAddress.city : 'blank' }, State: { entry.ownerAddress.state ? entry.ownerAddress.state : 'blank' } Zip: { entry.ownerAddress.zip ? entry.ownerAddress.zip : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-13`}>Phone: { entry.ownerAddress.phone ? entry.ownerAddress.phone : 'blank' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-14`}>Handler: { entry.handler ? entry.handler : 'same as owner' }</Grid>
                <Grid item id={`entry-form-review-entries-${index}-line-15`} className="entry-form-review-bold">Email: { entry.email }</Grid>
                <Grid item> Classes Entered:</Grid>
                { getClasses(entry.eventEntries).map((classEntered: string, classIndex: number) => (
                  <Grid item key={`entry-form-review-entries-${index}-class-${classIndex}`} id={`entry-form-review-entries-${index}-class-${classIndex}`}>{ classEntered }</Grid>
                ))}
                <Grid item id={`entry-form-review-entries-${index}-entry-fee`}>Entry Fee for this dog: ${ getEntryFee(entry)}</Grid>
              </Grid>
            ))}
          </Grid>
          <Grid container className='noprint'>
            <EntryFormAgreement />
          </Grid>
          <Grid container spacing={2} justifyContent="center" p={2} className='noprint'>
            <Grid item md={4} p={2}>
              <Button 
                id="entry-form-review-fix-data-button"
                color="primary" 
                variant="contained"
                style={{textTransform: 'none'}}
                onClick={(e: any) => { dispatch(setStepIndex(0)); dispatch(setAgreeToAgreementDate('')) }}>
                Oopps, Return to Step 1 to correct data
              </Button>
            </Grid>
            <Grid item md={3} p={2}>
              <Button 
                id="entry-form-review-proceed-button"
                color="primary" 
                variant="contained"
                disabled={agreeToAgreementDate === ''}
                style={{textTransform: 'none'}}
                onClick={(e: any) => dispatch(setStepIndex(2))}>
                Looks Good, Proceed to Payment
              </Button>
            </Grid>
          </Grid>
          <EntryFormPrint />
        </>
      }
    </>
  )
}
